import { filter, take } from 'rxjs/operators';
import { Component, ElementRef, ViewEncapsulation, OnInit, AfterViewInit, Inject, OnDestroy } from '@angular/core';
import { DialogPanelComponentContent } from '../../models/dialogs/DialogPanelComponentContent';
import { Router } from '@angular/router';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, Subscription } from 'rxjs';
import { UsersState } from '../../state-management/users/users.reducer';
import { AppState } from '../../interfaces/app-state.interface';
import { Store } from '@ngrx/store';
import { LoginWraperService } from '../../services/login.wraper.service';
import { SettingsService } from '../../services/settings/settings.service';
import { UsersAction } from '../../state-management/users/users.actions';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Roles as RolesUser } from '../../models/roles/roles.enum';
import { TrackingActions } from '../../state-management/tracking/tracking.action';
import { ActionEventsEnum } from '../../enum/tracking/action-events.enum';
import { SantanderSuitabilityService } from '../../services/santander-suitability/santander-suitability-service';
import { StorageService } from '../../services/storage.service';
import { getProfileDisplayName } from '../../resources/profile.utils';

const exitWebApp: any = null;

@AutoUnsubscribe()
@Component({
    selector: 'tr-side-menu',
    styleUrls: ['./tr-side-menu.component.scss'],
    templateUrl: './tr-side-menu.component.html',
    encapsulation: ViewEncapsulation.None,
})
export class TrSideMenuComponent extends DialogPanelComponentContent implements OnInit, AfterViewInit, OnDestroy {

    public logged: boolean;
    public hasToroRadar: boolean = true;
    public authentication$: Observable<UsersState>;
    public user$: Observable<UsersState>;
    public registerAppUrl: string = '';
    public userRole: string;
    public RolesUser = RolesUser;
    private authenticationSubscription: Subscription;
    private suitabilitySantanderSubscription: Subscription;
    public isPreAnticipation: boolean;
    public mustShowSantanderSuitabilityCommunication: boolean;
    public clientSegment: string;
    public hasProfile: boolean = null;
    public isExcludedFromSuitabilityCommunication: boolean = null;
    public currentUser: any;
    public displayName: string;
    private userProfileSubscription: Subscription;

    public get insideWebAppEnvironment(): boolean {
        return typeof exitWebApp === 'function';
    }
    constructor(
        element: ElementRef,
        private router: Router,
        private dialogReference: MatDialogRef<TrSideMenuComponent>,
        private store: Store<AppState>,
        private loginWraper: LoginWraperService,
        @Inject(MAT_DIALOG_DATA) private external: boolean,
        private settingsService: SettingsService,
        private santanderSuitabilityService: SantanderSuitabilityService,
    ) {

        super(element, true);
        this.dialogReference.addPanelClass('tr-side-menu-panel');
        this.user$ = this.store.select((s) => s.authentication);
        this.registerAppUrl = this.settingsService.getConfigurationKeyValue('rau');

        this.suitabilitySantanderSubscription = this.store
            .select((state) => state.suitabilitySantander)
            .pipe(filter((r) => !!r))
            .subscribe((r) => {
                this.hasProfile = r.profile?.profileName && r.profile.profileName.trim() !== '';
                this.isExcludedFromSuitabilityCommunication = r.isExcludedFromCommunication;
            }
        );

        this.authenticationSubscription = this.user$.pipe(
            filter((state) => state.currentUser && state.currentUser.token))
            .subscribe((state) => {
                this.logged = state.currentUser.token;
                this.userRole = state.currentUser.role;
            });

        this.getSuitabilityCommunicationConfig();
    }

    public ngOnInit() {
        super.ngOnInit();

        this.store
            .select((state) => state.authentication)
            .pipe(
                filter(({ currentUser }) => !!currentUser),
                take(1)
            )
            .subscribe(({ currentUser }) => (this.currentUser = currentUser));

        this.userProfileSubscription = this.store
            .select((state) => state.userProfile.user)
            .pipe(filter((user) => !!user))
            .subscribe((user: any) => (this.displayName = getProfileDisplayName(user)));
    }

    public ngAfterViewInit() {
        if (this.currentUser) {
            const homeType = this.resolveHomeType();
            const scenario = homeType === 'Aberta' && this.santanderSuitabilityService.getClientSegment() === 'Trader'
                ? 'Trader'
                : undefined;
            this.store.dispatch({
                type: TrackingActions.trackEvents,
                payload: {
                    type: ActionEventsEnum.ELEMENT_CLICKED,
                    events: {
                        'Product Area Origin': "Home",
                        Element: "Botão Menu mais",
                        Group: "Header",
                        'Time Responsável': "Sky",
                        ...(homeType === 'Aberta' && { 'Status': 'Aberta' }),
                        ...(scenario && { 'Scenario': scenario }),
                        'Specific Properties': {
                            home_type: homeType,
                            user_type: this.santanderSuitabilityService.getClientSegment(),
                        }
                    }
                },
            });
        }
        this.router.events.subscribe(() => this.closeMenu());
        this.lockBackgroundScroll();
    }

    private resolveHomeType(): string {
        const status = Number(StorageService.getCookie('hubUserStatus'));
        if (status === 9 || status === 10) return 'Ativa';
        if (status === 8) return 'Aberta';
        if (status >= 1 && status <= 7) return 'Identificada';

        if (this.currentUser?.role === 'Activated') return 'Ativa';
        if (this.currentUser?.role === 'Registered') return 'Aberta';
        return 'Identificada';
    }

    private lockBackgroundScroll() {
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
    }

    private unlockBackgroundScroll() {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
    }

    public closeMenu() {
        this.unlockBackgroundScroll();
        super.close(this.dialogReference, null);
    }

    public login() {
        this.closeMenu();
        this.loginWraper.navigateToLogin();
    }

    /** Realiza a navegação da url requisitada */
    public redirect(url: string) {

        if (
            this.external
            || url.equals('/bolsa/investir')
            || url.equals('/fundos/investir')
            || url.equals('/produtos-renda-fixa/investir')
            || new RegExp('/home').test(window.location.href)
        ) {

            this.redirectExternal(url);

        } else {
            this.router.navigateByUrl(url);
        }
    }

    public goToSuitabilitySantander() {
        this.store.dispatch({
            type: TrackingActions.trackEvents, payload:
            {
                type: ActionEventsEnum.ELEMENT_CLICKED,
                events: {
                    'Product Area Origin': 'Menu Mais',
                    'Element': 'Card atualizar Perfil de Investidor',
                    'Group': 'Card',
                    'Step': 'Não se aplica',
                    'Scenario': 'Pré-virada Suitability',
                    'Status': this.clientSegment,
                    'Label': 'Não se aplica',
                    'Time Responsável': 'Brooklyn Dumbo',
                    'Text': this.clientSegment == 'Trader' ?
                        'As regras do Perfil de Investidor vão mudar. Atualize o seu para continuar operando Day Trade.' :
                        'As regras do Perfil de Investidor vão mudar. Atualize seu perfil agora para continuar investindo.'
                }
            }
        });

        this.router.navigateByUrl('novo-suitability/introducao');
    }

    private getSuitabilityCommunicationConfig() {
        this.mustShowSantanderSuitabilityCommunication = this.santanderSuitabilityService.mustShowCommunication() && !this.isExcludedFromSuitabilityCommunication;
        this.isPreAnticipation = this.santanderSuitabilityService.isInPreAnticipation();
        this.clientSegment =  this.santanderSuitabilityService.getClientSegment();
    }

    public openExternalHrefLink(param: string) {
        window.open(param);
    }

    public logout() {
        this.store.dispatch({ type: UsersAction.logout, payload: { externalRouter: this.external } });
        // Função do ambiente do WebApp
        if (this.insideWebAppEnvironment) {
            exitWebApp();
        }
    }

    // não remover, necessário para o funcionamento correto do @AutoUnsubscribe
    // tslint:disable-next-line:no-empty
    // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method, @typescript-eslint/no-empty-function
    public ngOnDestroy() {
        this.unlockBackgroundScroll();
    }

    private redirectExternal(url: string) {

        if (window.location.href !== url) {
            window.location.href = url;
        }
    }
}
